'use client';

import { Box, ButtonBase, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { qrcodeUrl } from '@verifime/utils';
import { ReactElement, useState } from 'react';

/** Board slot 25 (approved copy, not renamed with a board slot number here since this is the
 *  sole consumer): the revealed panel's caption, telling the customer to act before selecting
 *  Continue because the link works once. */
const QR_HANDOFF_INSTRUCTION =
  'Scan this with your phone to carry on there. Do it before you select Continue: this link ' +
  'works once and we cannot send another.';

export type DesktopQrRevealProps = Readonly<{
  /** The same unspent link the page itself was loaded with, in /token/<jwt> path form. Never a
   *  fragment: a fragment is client-only per RFC 3986 section 3.5, and is not reliably preserved
   *  by a QR scanner, an email client, or an HTTP redirect. Possible only because nothing is
   *  spent on load (this component's whole premise, shared with MagicLinkContinue). */
  linkUrl: string;
  /** Overrides the rendered QR image's `src`, which otherwise derives from `qrcodeUrl(linkUrl)`.
   *  Exists so a board/story caller can supply a hardcoded `data:` URI instead: a pixel
   *  comparison whose threshold is 0 and which fetches an image over the network is
   *  non-deterministic by construction, so board stories pass this to keep the capture free of
   *  network fetches. Every other caller leaves it unset and gets the derived value. */
  qrImageUrl?: string;
  'data-testid'?: string;
}>;

/**
 * The branded hosted QR code (qrcodeUrl, @verifime/utils) beside the desktop webcam requirement,
 * collapsed by default and revealed in place, never in a dialog: this is the affordance a
 * customer reaches for when their desktop has no camera, so it must not interrupt the primary
 * Continue path with a modal.
 *
 * D6: renders nothing at all when NEXT_PUBLIC_QRCODE_URL_PREFIX is not configured for this app,
 * rather than calling qrcodeUrl and letting it throw. This is not a fallback UI: a missing QR
 * reveal on this screen is a silent, correct degrade, since the requirement it answers (no camera
 * on this machine) is desktop-only and the primary Continue path is unaffected. qrcodeUrl itself
 * keeps throwing when its prefix is missing: it is right to tell a caller that asked for
 * something impossible, and this component is the caller that guards it.
 *
 * Deliberately does not use QRCodeComponent (packages/components/src/QRCodeComponent.tsx): that
 * component renders client-side from the `qrcode` npm package at one of three fixed sizes, with
 * no branding and no centre mark, and exists for authenticator enrolment. This is the branded
 * hosted generator instead.
 *
 * The closed state is one sentence, matching the board's single `<p class="qrcopy">` with an
 * inline `<span class="qraction">`: the reveal action renders as a MUI `ButtonBase`, stripped of
 * chrome, rather than the board's own `<span>`. The board is a static mock with nothing to click;
 * ours reveals a QR code, so the control must stay a real, keyboard-reachable `<button>`
 * regardless of what the board's markup says, matching its appearance without giving up its
 * semantics.
 */
function DesktopQrRevealComponent({
  linkUrl,
  qrImageUrl,
  'data-testid': dataTestId,
}: DesktopQrRevealProps): ReactElement | null {
  const [revealed, setRevealed] = useState(false);

  if (!process.env.NEXT_PUBLIC_QRCODE_URL_PREFIX) {
    return null;
  }

  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        paddingTop: theme.semantic.spacing.sm,
        // The same expression IdentityRequirementSummary's own card border uses (see that
        // component's card Stack), not a hardcoded token name: the board recolours the card
        // border and this divider TOGETHER in dark mode (from Eggplant 200 to a neutral grey), so
        // tracking the card's own expression is what keeps the two matched in both modes if the
        // token is ever retuned. Verified against the board's own computed colours (its base
        // stylesheet rule alone reads as one purple in both modes; a dark-mode override further
        // down the same stylesheet reassigns both the card border and this divider to the same
        // neutral): light rgb(191, 182, 253) / #bfb6fd, dark rgb(71, 75, 85) / #474b55, both an
        // exact match.
        borderTop: `1px solid ${theme.palette.border.subtleAccent}`,
        gap: theme.semantic.spacing.sm,
        alignItems: revealed ? 'center' : 'flex-start',
      })}
    >
      {revealed ? (
        <>
          <Typography variant="body2" color="text.tertiary">
            {QR_HANDOFF_INSTRUCTION}
          </Typography>
          <Box
            component="img"
            src={qrImageUrl ?? qrcodeUrl(linkUrl)}
            alt="QR code that opens this page on a phone"
            sx={(theme: Theme) => ({
              width: '240px',
              height: '240px',
              // The QR frame's own border, distinct from the divider above: the board gives it a
              // neutral (--color-neutral-100), not the card's brand-tinted primary-200, so this
              // stays on the theme's generic divider token rather than border.subtleAccent (see
              // the comment on the divider above). Left unchanged.
              border: `1px solid ${theme.palette.divider}`,
              padding: theme.semantic.spacing['3xs'],
              boxSizing: 'border-box',
            })}
          />
        </>
      ) : (
        // One paragraph, matching the board's single <p class="qrcopy">: the question and the
        // reveal action read as one sentence on one line, not two stacked rows.
        <Typography variant="body2" color="text.tertiary">
          No camera on this machine?{' '}
          <ButtonBase
            type="button"
            data-action="reveal-desktop-qr"
            onClick={() => setRevealed(true)}
            // The board's static <span> has no click affordance to echo (no ripple, no hover
            // state): a ripple here would be new visual noise the board never had, on a control
            // whose whole job is to sit quietly inline in a sentence.
            disableRipple
            sx={(theme: Theme) => ({
              display: 'inline',
              // Inherits size/family/line-height from the surrounding Typography rather than the
              // board's literal 13px/1.4: this package's type scale has no 13px step (the nearest
              // tokens are fontSizeXs at 12px and fontSizeSm, already used here, at 14px), so
              // inheriting the paragraph's own already-established size keeps the question and
              // the action reading as one sentence rather than introducing a second, untokenised
              // literal alongside it.
              fontSize: 'inherit',
              fontFamily: 'inherit',
              lineHeight: 'inherit',
              // The board's `.qraction`: 600 weight, `--text-primary` colour, against the
              // question's 400-weight tertiary tone above.
              fontWeight: theme.semantic.fontWeight.semibold,
              // NOT theme.palette.text.primary, even though the board's own rule literally says
              // `color: var(--text-primary)`. That is a name collision between two token
              // systems, not a hint to follow: the board's `--text-primary` computes to the
              // brand purple (rgb(95, 72, 251)), while this theme's `text.primary` is
              // `clarityTokens.colorBlack` (rgb(0, 0, 0)) - same name, opposite end of the
              // palette. Map by resolved value, not by name: theme.palette.primary.main is
              // `generateColor(brandColorPrimary, '500', 'clarity')` = #5f48fb, an exact match
              // for the board's measured value (the dark theme uses a lighter gradation of the
              // same ramp, so #5f48fb is the light-theme value only, not primary.main in general).
              color: theme.palette.primary.main,
              // No existing focus-ring convention in this package to match (grepped: no
              // `focusVisible`/custom outline pattern on any inline or text-styled control). This
              // control strips every other piece of Button chrome (padding, min-width,
              // min-height, hover background, uppercase), which would otherwise leave it with no
              // visible focus indicator at all once that chrome is gone - failing the one
              // requirement the board's static span never had to meet. The 2px width is a literal
              // like the 1px borders already in this file, not a spacing token: outline width
              // isn't part of this package's spacing scale.
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: theme.semantic.spacing['3xs'],
              },
            })}
          >
            Show a code to scan with your phone
          </ButtonBase>
        </Typography>
      )}
    </Stack>
  );
}

export const DesktopQrReveal = withClarityTheme(DesktopQrRevealComponent);
